import { Link } from "react-router-dom";
import { ArrowRight, Compass, Bell, Users2 } from "lucide-react";
import { useAuth } from "@/store/AuthContext";

export default function Landing() {
  const { login } = useAuth();
  return (
    <div className="min-h-screen bg-paper text-charcoal flex flex-col" data-testid="landing-page">
      <header className="border-b border-stone-200">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-full bg-charcoal text-paper flex items-center justify-center font-serif text-lg leading-none">c</span>
            <span className="font-serif text-2xl leading-none tracking-tight">Cadence</span>
          </Link>
          <button onClick={login} className="btn-outline min-h-[2.25rem] py-1 text-xs" data-testid="landing-signin-header">
            Sign in
          </button>
        </div>
      </header>

      <main className="flex-1">
        <section className="max-w-6xl mx-auto px-5 md:px-8 pt-16 md:pt-24 pb-20">
          <span className="eyebrow">A quiet circle of timed habits</span>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight text-stone-900 mt-4 leading-[1.02] max-w-3xl">
            Keep small habits, <span className="italic text-terracotta">together.</span>
          </h1>
          <p className="text-stone-600 mt-6 max-w-xl text-lg leading-relaxed">
            Pick a few timed rituals. Cadence quietly assembles a circle of like-minded people whose rhythm resembles yours, then keeps you honest with gentle peer nudges.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button onClick={login} className="btn-accent" data-testid="landing-signin-cta">
              Sign in with Google <ArrowRight size={16} />
            </button>
            <span className="text-xs text-stone-500">Free · no card required</span>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-5 md:px-8 pb-24 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: Users2, title: "Matched by rhythm", body: "Your habits and signals put you in a circle of ~6 who share your hours." },
            { icon: Compass, title: "One shared calendar", body: "Every member's timed habit shows on a single week — kept by many hands." },
            { icon: Bell, title: "Nudges, not alarms", body: "A soft pulse when a friend's habit is close. No streaks, no shaming." },
          ].map(({ icon: Icon, title, body }) => (
            <article key={title} className="card-editorial p-6">
              <Icon size={22} className="text-terracotta" />
              <h3 className="font-serif text-2xl text-stone-900 mt-3">{title}</h3>
              <p className="text-sm text-stone-600 mt-2 leading-relaxed">{body}</p>
            </article>
          ))}
        </section>

        <section
          className="max-w-6xl mx-auto px-5 md:px-8 pb-24"
          data-testid="landing-social-proof"
          aria-label="What members are saying"
        >
          <span className="eyebrow">A quiet week, kept together</span>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                quote:
                  "Amara in Lisbon kept 12 mornings with her circle last month.",
                who: "Lisbon Dawn Circle · 7 members",
              },
              {
                quote:
                  "Kenji finished 38 evening sketches — nudged softly by Tokyo.",
                who: "Tokyo Dusk Studio · 6 members",
              },
              {
                quote:
                  "Chidera and Kwame closed 21 deep-work blocks before 10 a.m.",
                who: "Lagos Bright Line · 8 members",
              },
            ].map((t) => (
              <blockquote
                key={t.who}
                className="border-l-2 border-terracotta pl-4 py-1"
                data-testid="landing-social-proof-quote"
              >
                <p className="font-serif text-lg text-stone-800 leading-snug italic">
                  “{t.quote}”
                </p>
                <footer className="eyebrow mt-3 text-stone-500 not-italic">
                  {t.who}
                </footer>
              </blockquote>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-4 text-xs text-stone-500">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-terracotta" />
              4 circles across 5 timezones
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-terracotta" />
              12 timed rituals in the library
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-terracotta" />
              No streaks · no shame
            </span>
          </div>
        </section>
      </main>

      <footer className="border-t border-stone-200">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-6 flex items-center justify-between">
          <p className="font-serif text-lg text-stone-700">Cadence &nbsp;·&nbsp; a quiet circle of timed habits.</p>
          <button onClick={login} className="text-sm text-terracotta hover:underline" data-testid="landing-signin-footer">
            Sign in →
          </button>
        </div>
      </footer>
    </div>
  );
}
